const FINANCIAL_ACTION_REPLAY_WINDOW_MS = 30_000;

type FinancialActionFingerprintInput = {
  action: string;
  payload: Record<string, unknown>;
};

type CachedFinancialActionKey = {
  idempotencyKey: string;
  expiresAtMs: number;
};

const recentIntentKeys = new Map<string, CachedFinancialActionKey>();

function stableStringify(value: unknown): string {
  if (value === null || value === undefined) {
    return 'null';
  }

  if (typeof value !== 'object') {
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringify(item)).join(',')}]`;
  }

  const entries = Object.entries(value as Record<string, unknown>)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, entry]) => `${JSON.stringify(key)}:${stableStringify(entry)}`);
  return `{${entries.join(',')}}`;
}

function buildFingerprint({ action, payload }: FinancialActionFingerprintInput): string {
  return `${action}:${stableStringify(payload)}`;
}

function pruneExpiredKeys(nowMs: number): void {
  for (const [fingerprint, cacheEntry] of recentIntentKeys.entries()) {
    if (cacheEntry.expiresAtMs <= nowMs) {
      recentIntentKeys.delete(fingerprint);
    }
  }
}

function createIdempotencyKey(): string {
  return globalThis.crypto?.randomUUID?.()
    || `financial-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export type FinancialActionIdempotencyOptions = {
  action: string;
  payload: Record<string, unknown>;
  idempotencyKey?: string;
};

export function getFinancialActionIdempotencyKey({
  action,
  payload,
  idempotencyKey,
}: FinancialActionIdempotencyOptions): string {
  if (idempotencyKey) {
    return idempotencyKey;
  }

  const nowMs = Date.now();
  pruneExpiredKeys(nowMs);

  const fingerprint = buildFingerprint({ action, payload });
  const existing = recentIntentKeys.get(fingerprint);

  if (existing && existing.expiresAtMs > nowMs) {
    return existing.idempotencyKey;
  }

  const generated = createIdempotencyKey();
  recentIntentKeys.set(fingerprint, {
    idempotencyKey: generated,
    expiresAtMs: nowMs + FINANCIAL_ACTION_REPLAY_WINDOW_MS,
  });

  return generated;
}

export function resetFinancialActionIdempotencyCacheForTests(): void {
  recentIntentKeys.clear();
}
